import React from 'react';
import { motion } from 'framer-motion';

interface CaseStudy {
  id: number;
  title: string;
  description: string;
  problem: string;
  solution: string;
  results: string[];
}

const CaseStudies: React.FC = () => {
  const caseStudies: CaseStudy[] = [
    {
      id: 1,
      title: "Content Creation Automation",
      description: "Blancc Media needed to efficiently transform long-form podcast content into social media posts.",
      problem: "They had to breakdown 3-4 hour long YouTube podcasts into Twitter posts, long forms and threads for their clients.",
      solution: "Made a custom AI assistant in make.com that takes those transcripts, divides them into chunks based on topics, and converts them into 3 kinds of content (one liners, short tweets & threads).",
      results: [
        "Saved 17-20 hours/week with automations",
        "Established standard processes for scaling content creation",
        "Maintained strict compliance in the supplement niche"
      ]
    },
    {
      id: 2,
      title: "Competitor Analysis",
      description: "Blancc Media needed insights into competitor strategies to improve their content performance.",
      problem: "They wanted to understand large chunks of competitor data (other influencers on X, what worked for them etc.)",
      solution: "Made an automation that analyzed 3 of their competitors' Twitter profiles, gathered 100 posts, where they could see the top performing ones based on engagement, likes, views, etc.",
      results: [
        "Identified top-performing content strategies",
        "Helped outperform influencers in the same niche",
        "Provided data-driven content direction"
      ]
    },
    {
      id: 3,
      title: "Social Media Content Conversion",
      description: "Blancc Media needed to repurpose Instagram content for Twitter efficiently.",
      problem: "They wanted to convert IG Reels to Twitter content without manual transcription and reformatting.",
      solution: "Created a block of code that generates transcript of the reel and tweets with viral hooks. 100% Automated with only manual work being inputting the IG link.",
      results: [
        "Eliminated manual transcription work",
        "Created consistent cross-platform content",
        "Generated viral hooks automatically"
      ]
    }
  ];

  return (
    <div className="bg-black py-24">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-6 text-center"
        >
          <div className="inline-block px-3 py-1 rounded-full bg-accent/10 border border-accent/20 text-accent mb-6">
            <span className="text-sm font-medium">Customer Stories</span>
          </div>
        </motion.div>
        
        <motion.h2 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="text-5xl font-bold text-center mb-6"
        >
          Scale faster than ever.
        </motion.h2>
        
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="text-xl text-gray-300 text-center max-w-3xl mx-auto mb-16"
        >
          Optimizing and simplifying your operations with custom automations & building using AI.
        </motion.p>
        
        <div className="space-y-16">
          {caseStudies.map((study, index) => (
            <motion.div
              key={study.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="bg-primary/20 backdrop-blur-sm rounded-xl overflow-hidden"
            >
              <div className="grid grid-cols-1 lg:grid-cols-2">
                <div className="p-8 lg:p-12">
                  <h3 className="text-3xl font-bold mb-4">{study.title}</h3>
                  <p className="text-gray-300 mb-8">{study.description}</p>
                  
                  <div className="space-y-6">
                    <div>
                      <h4 className="text-accent font-semibold mb-2">The Problem:</h4>
                      <p className="text-gray-300">{study.problem}</p>
                    </div>
                    
                    <div>
                      <h4 className="text-accent font-semibold mb-2">The Solution:</h4>
                      <p className="text-gray-300">{study.solution}</p>
                    </div>
                    
                    <div>
                      <h4 className="text-accent font-semibold mb-2">The Results:</h4>
                      <ul className="space-y-2">
                        {study.results.map((result, i) => (
                          <li key={i} className="flex items-start">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-accent mr-2 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                            <span className="text-gray-300">{result}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
                
                <div className="bg-primary/40 flex items-center justify-center p-8">
                  <div className="text-4xl font-bold text-gradient">Case Study {study.id}</div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CaseStudies;
